import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { MENU as allMenuItems } from '../../menuConfig';

export default function Sidebar({ currentPage, onNavigate, collapsed, onToggle, mobileOpen }) {
  const [expanded, setExpanded] = useState({});
  const { hasAccess } = useAuth();

  // Filter menu items based on user access
  const menuItems = useMemo(() => {
    return allMenuItems
      .map(item => {
        if (item.children) {
          const visibleChildren = item.children.filter(child => hasAccess(child.key));
          if (visibleChildren.length === 0) return null;
          return { ...item, children: visibleChildren };
        }
        return hasAccess(item.key) ? item : null;
      })
      .filter(Boolean);
  }, [hasAccess]);

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (key) => currentPage === key;
  const isParentActive = (item) => item.children?.some(c => c.key === currentPage);

  return (
    <aside className={`bg-white border-r border-gray-200 h-screen flex flex-col transition-all duration-200 flex-shrink-0 z-40 ${mobileOpen ? 'fixed inset-y-0 left-0 w-64 shadow-xl' : collapsed ? 'w-16 hidden md:flex' : 'w-64 hidden md:flex'}`}>
      <div className={`px-4 py-3 border-b border-gray-100 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <img src="/shivmaya-logo-dark.svg" alt="ShivMaya Tech Solutions" className="h-14 w-auto" />
        )}
        <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0">
          {collapsed ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {menuItems.map(item => {
          const Icon = item.icon;
          const hasChildren = item.children?.length > 0;
          const isExp = expanded[item.key] || isParentActive(item);

          if (!hasChildren) {
            return (
              <button
                key={item.key}
                onClick={() => onNavigate(item.key)}
                className={`sidebar-link w-full ${isActive(item.key) ? 'sidebar-link-active' : 'sidebar-link-inactive'}`}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={20} />
                {!collapsed && <span>{item.label}</span>}
              </button>
            );
          }

          return (
            <div key={item.key}>
              <button
                onClick={() => collapsed ? onNavigate(item.children[0].key) : toggleExpand(item.key)}
                className={`sidebar-link w-full ${isParentActive(item) ? 'text-blue-700' : 'sidebar-link-inactive'}`}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={20} />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {isExp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </>
                )}
              </button>
              {!collapsed && isExp && (
                <div className="ml-5 pl-3 border-l border-gray-200 space-y-0.5 mt-0.5">
                  {item.children.map(child => (
                    <button
                      key={child.key}
                      onClick={() => onNavigate(child.key)}
                      className={`sidebar-link w-full text-xs ${isActive(child.key) ? 'sidebar-link-active' : 'sidebar-link-inactive'}`}
                    >
                      {child.icon && <child.icon size={16} />}
                      <span>{child.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="p-3 border-t border-gray-100 text-center space-y-0.5">
          <div className="text-xs text-gray-400">v1.0 - MayaSoft ERP</div>
          <div className="text-[11px] text-gray-400">Developed by <span className="font-semibold text-blue-600">ShivMaya Tech Solutions</span></div>
        </div>
      )}
    </aside>
  );
}
